/**
 * AI 검토 결과의 섹션 파싱 유틸리티
 *
 * [약사용 보고서], [환자용 복약지도문] 섹션을 분리합니다.
 * [종사자용 안내문]은 [환자용 복약지도문]을 대체하는 마커로, 동일한 patient-side 섹션으로 취급합니다.
 */

export interface SectionContent {
  /** 섹션 마커 이전 내용 */
  beforeSections: string;
  /** [약사용 보고서] 섹션 (마커 포함) */
  pharmacistReport?: string;
  /** [환자용 복약지도문] 또는 이를 대체하는 [종사자용 안내문] 섹션 (마커 포함) */
  patientGuide?: string;
}

interface SectionMarker {
  type: 'pharmacist' | 'patient';
  index: number;
}

/**
 * AI 검토 결과 컨텐츠를 섹션별로 분리
 *
 * @param content - 원본 컨텐츠 (HTML 또는 Markdown)
 * @returns 섹션별로 분리된 컨텐츠
 *
 * @example
 * ```typescript
 * const sections = parseSectionContent(content);
 * if (sections.pharmacistReport) {
 *   // 약사용 보고서 섹션 처리
 * }
 * ```
 */
export const parseSectionContent = (content: string): SectionContent => {
  const sections: SectionContent = { beforeSections: '' };

  // 섹션 마커 패턴
  const pharmacistMarker = /\[약사용 보고서\]/;
  // 환자용 복약지도문("문" 선택적) 또는 이를 대체하는 종사자용 안내문
  const patientMarker = /\[환자용 복약지도문?\]|\[종사자용 안내문\]/;

  // 마커 찾기
  const pharmacistMatch = content.match(pharmacistMarker);
  const patientMatch = content.match(patientMarker);

  // 섹션이 없으면 전체를 beforeSections로 반환
  if (!pharmacistMatch && !patientMatch) {
    sections.beforeSections = content;
    return sections;
  }

  // 마커 위치 배열 생성 및 정렬
  const markers: SectionMarker[] = [];

  if (pharmacistMatch?.index !== undefined) {
    markers.push({ type: 'pharmacist', index: pharmacistMatch.index });
  }

  if (patientMatch?.index !== undefined) {
    markers.push({ type: 'patient', index: patientMatch.index });
  }

  // 나타나는 순서대로 정렬
  markers.sort((a, b) => a.index - b.index);

  // 첫 번째 섹션 이전 내용
  sections.beforeSections = content.substring(0, markers[0].index);

  // 각 섹션 추출 (현재 마커부터 다음 마커 전까지)
  for (let i = 0; i < markers.length; i++) {
    const currentMarker = markers[i];
    const nextMarker = markers[i + 1];

    const startIndex = currentMarker.index;
    const endIndex = nextMarker ? nextMarker.index : content.length;
    const sectionContent = content.substring(startIndex, endIndex);

    if (currentMarker.type === 'pharmacist') {
      sections.pharmacistReport = sectionContent;
    } else {
      sections.patientGuide = sectionContent;
    }
  }

  return sections;
};

/**
 * 섹션 존재 여부 확인
 *
 * @param content - 원본 컨텐츠
 * @returns 각 섹션의 존재 여부
 */
export const checkSectionExistence = (content: string) => {
  const sections = parseSectionContent(content);

  return {
    hasPharmacistReport: !!sections.pharmacistReport,
    hasPatientGuide: !!sections.patientGuide,
    // 환자용 자리(patient-side)가 종사자용 안내문으로 채워졌는지
    // 섹션이 종사자용 마커로 "시작"하는지로 판정 (parseSectionContent가 마커 위치부터 자름)
    // → 비정상 공존 시 섹션 내부 우연 매칭으로 인한 오판 방지
    isStaffGuide: !!sections.patientGuide && /^\s*\[종사자용 안내문\]/.test(sections.patientGuide),
  };
};
